import {
  ContractHistoryChange,
  ContractHistoryEditMetadata,
  ContractHistoryEntry,
  ParsedContractHistoryEntry,
} from "@/services/private-office-contracts-service/types";
import { formatCurrency } from "@/utils/format/currency";
import { formatInTimeZone } from "date-fns-tz";

const parseDate = (dateString: string): [string, string] => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }
    return formatInTimeZone(
      date,
      "America/Sao_Paulo",
      "dd/MM/yyyy HH:mm",
    ).split(" ") as [string, string];
  } catch {
    return ["Data inválida", ""];
  }
};

const fieldTranslations: Record<string, string> = {
  packageId: "ID do pacote",
  dealId: "Deal ID",
  startDate: "Data de início",
  endDate: "Data de término",
  members: "Num. Pessoas",
  monthlyCredits: "Créditos mensais",
  partnerPrice: "Valor do parceiro",
  takePercentage: "Take",
  repass: "Repasse",
  noticeInDaysCompany: "Aviso prévio da cliente",
  noticeInDaysPartner: "Aviso prévio do parceiro",
  notes: "Notas",
  renewalIndicator: "Indicador de renovação",
  partnerAssignmentTermId: "Termo de ciência",
  companyAssignmentTermId: "Termo de aceite",
};

const titleSuffixMap: Record<string, string> = {
  cycle: " via cancelamento de ciclo",
  credit_contract: " via cancelamento do contrato de crédito",
  default: "",
  package_renewal: " via renovação de pacote",
  notice: " via aviso prévio",
};

const translateFieldKey = (key: string): string => {
  const translation = fieldTranslations[key];
  return translation ? `${translation} alterado` : `${key} alterado`;
};

const EMPTY_VALUE = "—";

/** Decimals shown for the value informed before rounding. */
const RAW_VALUE_MAX_FRACTION_DIGITS = 6;

const roundingRuleLabels: Record<string, string> = {
  HALF_UP_2: "metade para cima, 2 casas",
  NONE: "não aplicado",
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;

  const numericValue = Number(value);
  return isNaN(numericValue) ? null : numericValue;
};

const formatDecimal = (value: unknown, maximumFractionDigits = 2): string => {
  const numericValue = toNumber(value);

  if (numericValue === null) return EMPTY_VALUE;

  return numericValue.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits,
  });
};

const formatMoney = (value: unknown, maximumFractionDigits = 2): string => {
  const numericValue = toNumber(value);

  if (numericValue === null) return EMPTY_VALUE;

  return formatCurrency(numericValue, false, 2, maximumFractionDigits);
};

const formatPercentage = (
  value: unknown,
  maximumFractionDigits = 2,
): string => {
  const formatted = formatDecimal(value, maximumFractionDigits);
  return formatted === EMPTY_VALUE ? formatted : `${formatted}%`;
};

const formatInteger = (value: unknown): string => {
  const numericValue = toNumber(value);

  if (numericValue === null) return EMPTY_VALUE;

  return numericValue.toLocaleString("pt-BR");
};

type ContractHistoryValueFormatter = (
  value: unknown,
  maximumFractionDigits?: number,
) => string;

/**
 * Fields whose from/to values are shown in the history. Other fields keep the
 * legacy `"<campo> alterado"` line.
 */
const fieldValueFormatters: Record<string, ContractHistoryValueFormatter> = {
  monthlyCredits: formatDecimal,
  partnerPrice: formatMoney,
  repass: formatMoney,
  takePercentage: formatPercentage,
  members: formatInteger,
};

const buildRoundingSuffix = (
  change: ContractHistoryChange,
  formatValue: ContractHistoryValueFormatter,
): string => {
  const ruleLabel = change.roundingRuleApplied
    ? roundingRuleLabels[change.roundingRuleApplied]
    : undefined;

  if (!ruleLabel) return "";

  const rawValueSuffix =
    change.rawValue === undefined
      ? ""
      : ` — informado ${formatValue(
          change.rawValue,
          RAW_VALUE_MAX_FRACTION_DIGITS,
        )}`;

  return ` (arredondamento: ${ruleLabel}${rawValueSuffix})`;
};

const parseChange = (change: ContractHistoryChange): string => {
  const formatValue = fieldValueFormatters[change.field];

  if (!formatValue) return translateFieldKey(change.field);

  const label = fieldTranslations[change.field] ?? change.field;
  const roundingSuffix = buildRoundingSuffix(change, formatValue);

  return `${label}: ${formatValue(change.previousValue)} → ${formatValue(
    change.newValue,
  )}${roundingSuffix}`;
};

const isEditMetadataWithChanges = (
  metadata: ContractHistoryEntry["metadata"],
): metadata is ContractHistoryEditMetadata =>
  Array.isArray((metadata as ContractHistoryEditMetadata)?.changes);

const parseEditedFields = (
  metadata: ContractHistoryEntry["metadata"],
): string[] =>
  isEditMetadataWithChanges(metadata)
    ? metadata.changes.map(parseChange)
    : Object.keys(metadata ?? {}).map(translateFieldKey);

export const parseContractHistory = (
  entries: ContractHistoryEntry[],
): ParsedContractHistoryEntry[] => {
  const today = new Date();

  return entries
    .filter((entry) => {
      if (!entry.date) return false;

      const entryDate = new Date(entry.date);
      if (isNaN(entryDate.getTime())) return false;

      return entryDate <= today;
    })
    .map((entry) => {
      const [date, time] = parseDate(entry.date);
      const formattedDate = time ? `${date} às ${time}` : date;

      switch (entry.type) {
        case "created":
          return {
            title: `Contrato criado`,
            subtitle: `${formattedDate} por ${entry.user.firstName} ${entry.user.lastName}`,
            iconColor: "bg-green-600",
          };
        case "edit": {
          const editTitleSuffix =
            titleSuffixMap[entry.origin] ?? ` via ${entry.origin}`;

          return {
            title: `Contrato editado${editTitleSuffix}`,
            subtitle: `${formattedDate} por ${entry.user.firstName} ${entry.user.lastName}`,
            iconColor: "bg-green-600",
            alteredFields: parseEditedFields(entry.metadata),
          };
        }
        case "package_renewal":
          return {
            title: `Renovação de pacote`,
            subtitle: `${formattedDate} por ${entry.user.firstName} ${entry.user.lastName}`,
            iconColor: "bg-green-600",
            alteredFields: [
              `ID do novo pacote: ${entry.metadata.newPackageId}`,
              `ID do pacote anterior: ${entry.metadata.parentPackageId}`,
            ],
          };
        case "cancel": {
          const cancelTitleSuffix =
            titleSuffixMap[entry.origin] ?? ` via ${entry.origin}`;

          return {
            title: `Contrato cancelado${cancelTitleSuffix}`,
            subtitle: formattedDate,
            iconColor: "bg-red-600",
            alteredFields: [
              `Data de cancelamento: ${parseDate(entry.metadata.cancelledAt)[0]}`,
            ],
          };
        }
        case "notice":
          return {
            title: `Aviso prévio`,
            subtitle: `${formattedDate} por ${entry.user.firstName} ${entry.user.lastName}`,
            iconColor: "bg-green-600",
            alteredFields: [
              `${entry.metadata.noticePeriodDays} dias de aviso prévio.`,
            ],
          };
        default:
          return {
            title: entry.type,
            subtitle: `${formattedDate} por ${entry.user.firstName} ${entry.user.lastName}`,
            iconColor: "bg-green-600",
          };
      }
    });
};
